import type { Block, BlockType } from "./blocks";
import type { PortalSchema } from "./portal-schema";

// Walks a page's own blocks plus one level into container blocks (section, iframe's fallback
// blocks) — the same depth PortalContent.tsx's own ad-hoc popup-collection loop already walks.
const pageContainsBlockType = (blocks: Block[], blockType: BlockType): boolean =>
  blocks.some((block) => {
    if (block.type === blockType) return true;
    if (block.type === "section") return block.blocks.some((child) => child.type === blockType);
    if (block.type === "iframe") return block.fallbackBlocks.some((child) => child.type === blockType);
    return false;
  });

/** Whether a block of the given type exists anywhere in the schema — any top-level page, any sub-page, or one level into a container block. */
export const schemaContainsBlockType = (schema: PortalSchema, blockType: BlockType): boolean =>
  schema.pages.some(
    (page) =>
      pageContainsBlockType(page.blocks, blockType) ||
      (page.pages ?? []).some((child) => pageContainsBlockType(child.blocks, blockType))
  );

export interface PageContainingBlockType {
  pageId: string;
  slug: string;
}

/** The page (or sub-page) that contains a block of the given type, or null if none does. */
export const findPageContainingBlockType = (
  schema: PortalSchema,
  blockType: BlockType
): PageContainingBlockType | null => {
  for (const page of schema.pages) {
    if (pageContainsBlockType(page.blocks, blockType)) {
      return { pageId: page.id, slug: page.slug };
    }
    const matchingChild = (page.pages ?? []).find((child) => pageContainsBlockType(child.blocks, blockType));
    if (matchingChild) {
      return { pageId: matchingChild.id, slug: matchingChild.slug };
    }
  }
  return null;
};
